import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { ResponsiveContainer, ComposedChart, Line, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine } from 'recharts';
import { format, parseISO } from 'date-fns';
import { FormControl, Select, MenuItem, Button } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ProductSelector from './ProductSelector';
import '../styles/PostAnalytics.css';
import { apiPost } from '../services/apiClient';
import { useStockSeries } from '../hooks/useStockSeries';
import { useStockPremium } from '../hooks/useStockPremium';
import {
    mergeStockIntoRows, mergePremiumIntoRows, benchmarkLabel, periodForMonths,
    STOCK_COLOR, STOCK_DASH_STRING, PREMIUM_MODES,
    formatStockPrice, formatPremiumPct,
} from '../utils/stockOverlay';

const ROLLING_AVG_OPTIONS = [
    { value: 5, label: '5 Days' },
    { value: 14, label: '14 Days' },
    { value: 30, label: '30 Days' },
    { value: 60, label: '60 Days' },
    { value: 90, label: '90 Days' },
    { value: 180, label: '180 Days' },
    { value: 365, label: '365 Days' }
];

const BUCKET_PERIOD_OPTIONS = [
    { value: 'day', label: 'Daily' },
    { value: 'week', label: 'Weekly' },
    { value: 'month', label: 'Monthly' }
];

const PostLikesAnalytics = ({ topicMap, companyName, companyTicker, autoSearch = false,
    initialStartDate, initialEndDate, initialBucket,
    // Optional controlled product selection, so a parent (e.g. the Daily
    // Trackers tab switcher) can keep the selected topic sticky across
    // different chart types. Falls back to internal state when omitted.
    selectedProduct: controlledSelectedProduct, onProductChange }) => {
    const [data, setData] = useState([]);
    // Overlay mode: 'off' | 'price' | 'peers' | 'snp500' | 'sector'.
    const [overlayMode, setOverlayMode] = useState('off');
    const [internalSelectedProduct, setInternalSelectedProduct] = useState('');
    const isProductControlled = controlledSelectedProduct !== undefined && typeof onProductChange === 'function';
    const selectedProduct = isProductControlled ? controlledSelectedProduct : internalSelectedProduct;
    const setSelectedProduct = isProductControlled ? onProductChange : setInternalSelectedProduct;
    const [rollingDays, setRollingDays] = useState(90);
    const [bucketPeriod, setBucketPeriod] = useState(initialBucket || 'month');
    const [isLoading, setIsLoading] = useState(false);

    const today = new Date();

    const [startDate, setStartDate] = useState(initialStartDate || '2024-01-01');
    const [endDate, setEndDate] = useState(initialEndDate || today.toISOString().split('T')[0]);
    const [isHeaderHovered, setIsHeaderHovered] = useState(false);

    const filteredTopicMap = useMemo(() => {
        if (!topicMap) return {};

        if (Object.keys(topicMap).length > 0 && !selectedProduct) {
            const sortedTopics = Object.values(topicMap).sort((a, b) => {
                const priorityA = a.priority === undefined ? Infinity : a.priority;
                const priorityB = b.priority === undefined ? Infinity : b.priority;
                return priorityA - priorityB;
            });

            if (sortedTopics[0]) {
                setSelectedProduct(sortedTopics[0].id);
            }
        }

        return {
            ...topicMap,
            'all': {
                id: 'all',
                name: 'All Topics',
                priority: 9999
            }
        };
    }, [topicMap, selectedProduct]);

    const getSelectedProductName = () => {
        if (selectedProduct && filteredTopicMap && filteredTopicMap[selectedProduct]) {
            return filteredTopicMap[selectedProduct].name;
        }
        return 'Product';
    };

    const getBucketLabel = () => {
        const bucket = BUCKET_PERIOD_OPTIONS.find(b => b.value === bucketPeriod);
        return bucket ? bucket.label : 'Daily';
    };

    const buildMainTitle = () => {
        return `${getSelectedProductName()} · Post Likes`;
    };

    const buildSubline = () => {
        const dateRange = `${format(parseISO(startDate), 'MMM d, yyyy')} - ${format(parseISO(endDate), 'MMM d, yyyy')}`;
        return `${dateRange} • ${getBucketLabel()}`;
    };

    const handleSearch = useCallback(async () => {
        if (!selectedProduct) return;

        setIsLoading(true);
        try {
            let rawData = [];

            if (selectedProduct === 'all') {
                console.log('Fetching likes data for all topics...');
                const topicIds = Object.keys(topicMap);

                const allTopicData = await Promise.all(
                    topicIds.map(async (topicId) => {
                        try {
                            const response = await apiPost('topic/posts/get-daily-likes', {
                                topic_id: topicId,
                                start_date: startDate,
                                end_date: endDate,
                            });

                            if (!response.ok) {
                                throw new Error(`Backend error: ${response.status}`);
                            }

                            const json = await response.json();
                            return json.data ?? json ?? [];
                        } catch (error) {
                            console.error(`Error fetching likes data for topic ${topicId}:`, error);
                            return [];
                        }
                    })
                );

                const aggregatedData = {};
                allTopicData.forEach(topicData => {
                    if (!Array.isArray(topicData)) return;

                    topicData.forEach(item => {
                        const date = item.day;
                        const numLikes = typeof item.num_posts === 'bigint'
                            ? Number(item.num_posts.toString())
                            : Number(item.num_posts);

                        if (!aggregatedData[date]) {
                            aggregatedData[date] = {
                                day: date,
                                num_posts: 0
                            };
                        }

                        aggregatedData[date].num_posts += numLikes;
                    });
                });

                rawData = Object.values(aggregatedData);
                console.log('Aggregated likes data for all topics:', rawData);

            } else {
                const response = await apiPost('topic/posts/get-daily-likes', {
                    topic_id: selectedProduct,
                    start_date: startDate,
                    end_date: endDate,
                });

                if (!response.ok) {
                    throw new Error(`Backend error: ${response.status}`);
                }

                const json = await response.json();
                rawData = json.data ?? json ?? [];
            }

            console.log('Raw likes data received:', rawData);

            if (!Array.isArray(rawData) || rawData.length === 0) {
                console.log('No likes data received or data is empty');
                setData([]);
                return;
            }

            const processedData = rawData.map(item => {
                const numLikes = typeof item.num_posts === 'bigint'
                    ? Number(item.num_posts.toString())
                    : Number(item.num_posts);

                return {
                    date: format(parseISO(item.day), 'MMM dd'),
                    fullDate: item.day,
                    likeCount: numLikes
                };
            });

            processedData.sort((a, b) => {
                const dateA = parseISO(a.fullDate);
                const dateB = parseISO(b.fullDate);
                return dateA - dateB;
            });

            let aggregatedData = processedData;

            if (bucketPeriod === 'week') {
                const weeklyData = [];
                let currentWeek = [];
                let currentWeekStart = null;

                processedData.forEach(item => {
                    const itemDate = parseISO(item.fullDate);
                    const weekStart = new Date(itemDate);
                    weekStart.setDate(weekStart.getDate() - weekStart.getDay());

                    if (!currentWeekStart || weekStart.getTime() !== currentWeekStart.getTime()) {
                        if (currentWeek.length > 0) {
                            const totalLikes = currentWeek.reduce((sum, day) => sum + day.likeCount, 0);
                            weeklyData.push({
                                date: format(currentWeekStart, 'MMM dd'),
                                fullDate: format(currentWeekStart, 'yyyy-MM-dd'),
                                likeCount: totalLikes
                            });
                        }
                        currentWeek = [item];
                        currentWeekStart = weekStart;
                    } else {
                        currentWeek.push(item);
                    }
                });

                if (currentWeek.length > 0) {
                    const totalLikes = currentWeek.reduce((sum, day) => sum + day.likeCount, 0);
                    weeklyData.push({
                        date: format(currentWeekStart, 'MMM dd'),
                        fullDate: format(currentWeekStart, 'yyyy-MM-dd'),
                        likeCount: totalLikes
                    });
                }

                aggregatedData = weeklyData;

            } else if (bucketPeriod === 'month') {
                const monthlyData = [];
                let currentMonth = [];
                let currentMonthKey = null;

                processedData.forEach(item => {
                    const itemDate = parseISO(item.fullDate);
                    const monthKey = format(itemDate, 'yyyy-MM');

                    if (currentMonthKey !== monthKey) {
                        if (currentMonth.length > 0) {
                            const totalLikes = currentMonth.reduce((sum, day) => sum + day.likeCount, 0);
                            const firstDayOfMonth = parseISO(currentMonth[0].fullDate);
                            monthlyData.push({
                                date: format(firstDayOfMonth, 'MMM yyyy'),
                                fullDate: format(firstDayOfMonth, 'yyyy-MM-dd'),
                                likeCount: totalLikes
                            });
                        }
                        currentMonth = [item];
                        currentMonthKey = monthKey;
                    } else {
                        currentMonth.push(item);
                    }
                });

                if (currentMonth.length > 0) {
                    const totalLikes = currentMonth.reduce((sum, day) => sum + day.likeCount, 0);
                    const firstDayOfMonth = parseISO(currentMonth[0].fullDate);
                    monthlyData.push({
                        date: format(firstDayOfMonth, 'MMM yyyy'),
                        fullDate: format(firstDayOfMonth, 'yyyy-MM-dd'),
                        likeCount: totalLikes
                    });
                }

                aggregatedData = monthlyData;
            }

            const dataWithRollingAvg = aggregatedData.map((item, index) => {
                let windowSize = rollingDays;
                if (bucketPeriod === 'week') {
                    windowSize = Math.ceil(rollingDays / 7);
                } else if (bucketPeriod === 'month') {
                    windowSize = Math.ceil(rollingDays / 30);
                }

                const startIndex = Math.max(0, index - (windowSize - 1));
                const rollingWindow = aggregatedData.slice(startIndex, index + 1);

                const sum = rollingWindow.reduce((acc, dayData) => acc + dayData.likeCount, 0);
                const average = sum / rollingWindow.length;

                return {
                    ...item,
                    rollingAvg: Math.round(average * 10) / 10
                };
            });

            console.log('Final processed likes data with rolling average:', dataWithRollingAvg);
            setData(dataWithRollingAvg);
        } catch (error) {
            console.error('Error in fetchData:', error);
            setData([]);
        } finally {
            setIsLoading(false);
        }
    }, [selectedProduct, startDate, endDate, rollingDays, bucketPeriod, topicMap]);

    // Capture mode: fire the search once automatically (no manual click) so the
    // chart renders when this component is mounted off-screen for the Report
    // Generator. Has no effect on the live page unless `autoSearch` is set.
    const autoSearchedRef = useRef(false);
    useEffect(() => {
        if (autoSearch && selectedProduct && !autoSearchedRef.current) {
            autoSearchedRef.current = true;
            handleSearch();
        }
    }, [autoSearch, selectedProduct, handleSearch]);

    // Stock overlay (publicly-traded companies only). 'price' shows the absolute
    // adjusted-close line; 'peers' / 'snp500' / 'sector' show a relative-
    // performance ("premium") comparison indexed to 100 at the window start.
    const priceMode = overlayMode === 'price';
    const premiumMode = ['peers', 'snp500', 'sector'].includes(overlayMode) ? overlayMode : null;

    // Request the premium window that matches the chart's visible date range so a
    // backend (period-start-baselined) premium lines up with what's shown.
    const premiumPeriod = useMemo(
        () => periodForMonths(Math.round((new Date(endDate) - new Date(startDate)) / (30 * 86400000))),
        [startDate, endDate]
    );
    const { series: stockSeries } = useStockSeries(priceMode ? companyTicker : null);
    const { payload: premium, error: premiumError } = useStockPremium(companyTicker, premiumMode, premiumPeriod);

    const showStockOverlay = Boolean(companyTicker) && priceMode;
    const showPremiumOverlay = Boolean(companyTicker) && Boolean(premiumMode) && Boolean(premium?.data?.length);
    const benchLabel = premium ? benchmarkLabel(premium) : '';
    const premiumName = premium ? `${companyTicker} vs ${benchLabel}` : '';

    const chartData = useMemo(() => {
        if (showStockOverlay) return mergeStockIntoRows(data, stockSeries, bucketPeriod);
        if (showPremiumOverlay) return mergePremiumIntoRows(data, premium.data, bucketPeriod);
        return data;
    }, [data, stockSeries, premium, bucketPeriod, showStockOverlay, showPremiumOverlay]);

    const CONTROL_HEIGHT = 36;
    const CONTROL_GAP = 12;

    const selectStyle = {
        width: '100%',
        height: CONTROL_HEIGHT,
        '& .MuiSelect-select': {
            paddingTop: '6px',
            paddingBottom: '6px',
            paddingRight: '24px !important',
        },
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#d0d0d0',
            transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#999',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#1976d2',
        }
    };

    const filterLabelStyle = {
        fontSize: '9px',
        fontWeight: 600,
        color: '#999',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        marginBottom: '3px'
    };

    const dateInputStyle = {
        padding: '6px 10px',
        borderRadius: '4px',
        border: '1px solid #d0d0d0',
        fontSize: '13px',
        height: CONTROL_HEIGHT,
        boxSizing: 'border-box',
        transition: 'border-color 0.15s ease',
        outline: 'none',
        width: '100%'
    };

    return (
        <div style={{ maxWidth: '100%', boxSizing: 'border-box' }}>
            {/* Header Card */}
            <div
                onMouseEnter={() => setIsHeaderHovered(true)}
                onMouseLeave={() => setIsHeaderHovered(false)}
                style={{
                    backgroundColor: '#fafafa',
                    borderRadius: '8px',
                    padding: '20px 24px 18px',
                    marginBottom: '20px',
                    boxShadow: isHeaderHovered
                        ? '0 4px 12px rgba(0, 0, 0, 0.08)'
                        : '0 1px 3px rgba(0, 0, 0, 0.06)',
                    border: '1px solid rgba(0, 0, 0, 0.06)',
                    transition: 'box-shadow 0.2s ease',
                    maxWidth: '100%',
                    boxSizing: 'border-box'
                }}
            >
                {/* Title Block */}
                <div style={{ marginBottom: '12px' }}>
                    <div style={{
                        fontSize: '11px',
                        color: '#777',
                        fontWeight: 500,
                        lineHeight: 1.2,
                        marginBottom: '1px'
                    }}>
                        {companyName || 'Company'}
                    </div>

                    <h1 style={{
                        fontSize: '22px',
                        fontWeight: 700,
                        margin: 0,
                        color: '#111',
                        lineHeight: 1.25
                    }}>
                        {selectedProduct ? buildMainTitle() : 'Select a Product'}
                    </h1>

                    {selectedProduct && (
                        <div style={{
                            fontSize: '12px',
                            color: '#999',
                            lineHeight: 1.3,
                            marginTop: '2px'
                        }}>
                            {buildSubline()}
                        </div>
                    )}
                </div>

                {/* Filter Bar */}
                <div style={{
                    display: 'flex',
                    alignItems: 'flex-end',
                    gap: `${CONTROL_GAP}px`,
                    flexWrap: 'wrap',
                    rowGap: '12px'
                }}>
                    {/* Product */}
                    <div style={{ flex: '1 1 auto', minWidth: 100, maxWidth: 160, boxSizing: 'border-box' }}>
                        <div style={filterLabelStyle}>Product</div>
                        <ProductSelector
                            topicMap={filteredTopicMap}
                            selectedId={selectedProduct || ''}
                            onSelect={setSelectedProduct}
                            label=""
                            size="small"
                            sx={selectStyle}
                        />
                    </div>

                    {/* Start Date */}
                    <div style={{ flex: '0 0 auto', minWidth: 110 }}>
                        <div style={filterLabelStyle}>Start Date</div>
                        <input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            max={endDate}
                            style={dateInputStyle}
                        />
                    </div>

                    {/* End Date */}
                    <div style={{ flex: '0 0 auto', minWidth: 110 }}>
                        <div style={filterLabelStyle}>End Date</div>
                        <input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            min={startDate}
                            max={new Date().toISOString().split('T')[0]}
                            style={dateInputStyle}
                        />
                    </div>

                    {/* Rolling Average */}
                    <div style={{ flex: '1 1 auto', minWidth: 90, maxWidth: 120 }}>
                        <div style={filterLabelStyle}>Rolling Avg</div>
                        <FormControl size="small" sx={selectStyle}>
                            <Select
                                value={rollingDays}
                                onChange={(e) => setRollingDays(Number(e.target.value))}
                            >
                                {ROLLING_AVG_OPTIONS.map(option => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </div>

                    {/* Bucket Period */}
                    <div style={{ flex: '1 1 auto', minWidth: 80, maxWidth: 110 }}>
                        <div style={filterLabelStyle}>Bucket</div>
                        <FormControl size="small" sx={selectStyle}>
                            <Select
                                value={bucketPeriod}
                                onChange={(e) => setBucketPeriod(e.target.value)}
                            >
                                {BUCKET_PERIOD_OPTIONS.map(option => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </div>

                    {/* Stock overlay (publicly-traded companies only): absolute
                        price, or a relative-performance comparison vs peers /
                        S&P 500 / sector. */}
                    {companyTicker && (
                        <div style={{ flex: '0 0 auto', alignSelf: 'flex-end' }}>
                            <div style={filterLabelStyle}>Overlay</div>
                            <FormControl size="small" sx={{ minWidth: 140 }}>
                                <Select
                                    value={overlayMode}
                                    onChange={(e) => setOverlayMode(e.target.value)}
                                    sx={{ height: CONTROL_HEIGHT }}
                                >
                                    <MenuItem value="off">Off</MenuItem>
                                    {PREMIUM_MODES.map((m) => (
                                        <MenuItem key={m.value} value={m.value}>
                                            {m.value === 'price' ? `Price (${companyTicker})` : m.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            {premiumMode && premiumError && (
                                <div style={{ fontSize: '11px', color: '#b00', marginTop: '2px', maxWidth: 140 }}>
                                    No comparison available
                                </div>
                            )}
                        </div>
                    )}

                    {/* Search Button */}
                    <div style={{ flex: '0 0 auto', alignSelf: 'flex-end' }}>
                        <Button
                            variant="contained"
                            onClick={handleSearch}
                            disabled={!selectedProduct || isLoading}
                            startIcon={<SearchIcon />}
                            sx={{
                                height: CONTROL_HEIGHT,
                                textTransform: 'none',
                                fontWeight: 600,
                                px: 2.5,
                            }}
                        >
                            {isLoading ? 'Loading...' : 'Search'}
                        </Button>
                    </div>
                </div>
            </div>

            {/* Chart Section */}
            <div style={{ marginTop: '8px', width: '100%', height: '500px' }}>
                <ResponsiveContainer>
                    <ComposedChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                            dataKey="date"
                            angle={-45}
                            textAnchor="end"
                            height={60}
                        />
                        <YAxis
                            yAxisId="left"
                            label={{
                                value: 'Number of Likes',
                                angle: -90,
                                position: 'insideLeft'
                            }}
                        />
                        {showStockOverlay && (
                            <YAxis
                                yAxisId="right"
                                orientation="right"
                                domain={['auto', 'auto']}
                                tickFormatter={formatStockPrice}
                                label={{
                                    value: 'Stock Price ($)',
                                    angle: 90,
                                    position: 'insideRight'
                                }}
                            />
                        )}
                        {showPremiumOverlay && (
                            <YAxis
                                yAxisId="right"
                                orientation="right"
                                domain={([dataMin, dataMax]) => [Math.min(0, dataMin), Math.max(0, dataMax)]}
                                tickFormatter={(v) => `${Math.round(v)}%`}
                                label={{
                                    value: 'Premium (%)',
                                    angle: 90,
                                    position: 'insideRight'
                                }}
                            />
                        )}
                        <Tooltip formatter={(value, name) => (
                            name === `Stock Price (${companyTicker})`
                                ? formatStockPrice(value)
                                : name === premiumName
                                    ? formatPremiumPct(value)
                                    : value
                        )} />
                        <Legend />
                        <Bar
                            yAxisId="left"
                            dataKey="likeCount"
                            fill="#82ca9d"
                            name="Like Count"
                        />
                        <Line
                            yAxisId="left"
                            type="monotone"
                            dataKey="rollingAvg"
                            stroke="#000000"
                            strokeWidth={2}
                            name={`${rollingDays}-Day Rolling Average`}
                            dot={{ fill: '#808080', strokeWidth: 1, r: 2 }}
                            activeDot={{ r: 6, stroke: '#ff7300', strokeWidth: 1 }}
                        />
                        {showStockOverlay && (
                            <Line
                                yAxisId="right"
                                type="monotone"
                                dataKey="stockPrice"
                                stroke={STOCK_COLOR}
                                strokeWidth={2}
                                strokeDasharray={STOCK_DASH_STRING}
                                name={`Stock Price (${companyTicker})`}
                                dot={false}
                                connectNulls
                            />
                        )}
                        {showPremiumOverlay && (
                            <ReferenceLine yAxisId="right" y={0} stroke="#bbb" strokeDasharray="3 3" />
                        )}
                        {showPremiumOverlay && (
                            <Line
                                yAxisId="right"
                                type="monotone"
                                dataKey="premiumPct"
                                stroke={STOCK_COLOR}
                                strokeWidth={2}
                                strokeDasharray={STOCK_DASH_STRING}
                                name={premiumName}
                                dot={false}
                                connectNulls
                            />
                        )}
                    </ComposedChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};

export default PostLikesAnalytics;
